import { Layout, Skeleton } from "antd";
import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./layout/sidebar";
import {
  MobileBottomNav,
  MobileBottomNavStyles,
  useMobileBottomNav,
} from "./layout/mobile-bottom-nav";
import { Wrapper } from "./style";
import { TableLoading } from "../components/table-animation";
import { BorderFilter } from "../config";
import { Admins } from "../routers/admin";
import { Users } from "../routers/user";
import { useThemeMode } from "../contexts/ThemeContext";
import {
  isSubUser,
  prepareSubUserSession,
  resolveLoginType,
  subUserNeedsSellerSwitch,
} from "../utils/subUser";
import {
  SubUserAccessProvider,
} from "../contexts/SubUserAccessContext";

const { Content } = Layout;

const Page = (props) => {
  const { user, setData } = props;
  const navigate = useNavigate();
  const location = useLocation();

  const [reloadPage, setReloadPage] = useState(true);
  const [loading, setLoading] = useState(true);
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const { themeMode } = useThemeMode();


  const handleLogout = () => {
    setData({});
    navigate("/login");
  };

  useEffect(() => {
    if (Object.keys(user)?.length === 0) {
      handleLogout();
    }

    setLoading(false);

    const handleStorage = (e) => {
      if (e?.storageArea?.length === 0) {
        setData({});
        return navigate("/login");
      }

      if (user) {
        const menusName = (
            !Object.keys(user)?.includes("admin") ? Admins : Users
        )?.map((d) => `/${d?.path}`);

        navigate(
            menusName?.includes(`${location.pathname}`)
                ? window.location.pathname
                : `${menusName?.[0]}`
        );
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [user]);

  useEffect(() => {
    if (!isSubUser(user) || !subUserNeedsSellerSwitch(user)) return;

    let cancelled = false;

    (async () => {
      try {
        const nextSession = await prepareSubUserSession(user?.user, user);
        if (!cancelled) setData(nextSession);
      } catch {
        // Keep existing session; user can switch manually from profile menu.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.token, user?.user?.user_data?.id, user?.user?.user_data?.seller_context_id]);

  // --- Subscription / Trial Banner ---
  const BANNER_HEIGHT = 40;
  const ud = user?.user?.user_data || {};
  const hasTrial         = !!ud.has_trial;
  const remainingTrialDays = ud.remaining_trial_days ?? 0;
  const isTrialExpired   = !!ud.is_trial_expired;
  const hasSubscription  = !!ud.has_subscription;
  const subEndsAt        = ud.subscription?.ends_at; // unix seconds

  let bannerMsg = null;
  let bannerBg  = '#314F71'; // default navy

  if (hasTrial && !isTrialExpired && !hasSubscription) {
    bannerMsg = `${remainingTrialDays} days left on your trial. Subscribe now, keep going.`;
  } else if (hasSubscription && subEndsAt) {
    // Subscription ending within 10 days
    const daysLeft = Math.ceil((subEndsAt * 1000 - Date.now()) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 10 && daysLeft > 0) {
      bannerBg  = '#314F71';
      bannerMsg = daysLeft === 1
        ? `Your subscription expires tomorrow. Renew to keep access.`
        : `Your subscription expires in ${daysLeft} days. Renew to keep access.`;
    }
  }

  // --- Logic for Breakpoints & Attributes ---
  useEffect(() => {
    const breakpoint = 992;
    document.body.setAttribute("data-kt-app-header-enabled", "false");
    document.body.setAttribute("data-kt-app-header-fixed", "false");

    const syncAttributes = () => {
      if (window.innerWidth < breakpoint) {
        document.body.setAttribute("data-kt-app-sidebar-minimize", "off");
        document.body.setAttribute(
            "data-kt-drawer",
            isSidebarMinimized ? "on" : "off"
        );
      } else {
        document.body.setAttribute(
            "data-kt-app-sidebar-minimize",
            isSidebarMinimized ? "on" : "off"
        );
        document.body.setAttribute("data-kt-drawer", "off");
      }
    };

    syncAttributes();
    window.addEventListener("resize", syncAttributes);
    return () => window.removeEventListener("resize", syncAttributes);
  }, [isSidebarMinimized]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--app-banner-height",
      bannerMsg ? `${BANNER_HEIGHT}px` : "0px"
    );
  }, [bannerMsg]);

  useEffect(() => {
    const sidebar = document.getElementById("kt_app_sidebar");
    const breakpoint = 992;

    const applySidebarClasses = () => {
      if (window.innerWidth < breakpoint) {
        sidebar?.classList.add("drawer", "drawer-start");
      } else {
        sidebar?.classList.remove("drawer", "drawer-start", "drawer-on");
        const overlay = document.getElementById("drawer-overlay");
        overlay?.remove();
      }
    };

    applySidebarClasses();
    window.addEventListener("resize", applySidebarClasses);
    return () => window.removeEventListener("resize", applySidebarClasses);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarMinimized((prev) => {
      const newState = !prev;
      const sidebar = document.getElementById("kt_app_sidebar");
      const overlayId = "drawer-overlay";
      const breakpoint = 992;

      if (window.innerWidth < breakpoint) {
        // Drawer logic for mobile < 992px
        let overlay = document.getElementById(overlayId);
        if (newState) {
          sidebar?.classList.add("drawer-on");
          if (!overlay) {
            overlay = document.createElement("div");
            overlay.id = overlayId;
            overlay.className = "drawer-overlay";
            overlay.style.cssText =
                `z-index:105; position:fixed; top:${bannerMsg ? BANNER_HEIGHT : 0}px; left:0; width:100%; height:100%; background:rgba(0,0,0,0.5); transition: opacity 0.3s ease;`;
            document.body.appendChild(overlay);
            overlay.addEventListener("click", () => {
              setIsSidebarMinimized(false);
              sidebar?.classList.remove("drawer-on");
              overlay.remove();
            });
          }
        } else {
          sidebar?.classList.remove("drawer-on");
          overlay?.remove();
        }
      } else {
        // Minimize logic for desktop >= 992px
        if (newState) {
          sidebar?.classList.add("sidebar-minimized");
        } else {
          sidebar?.classList.remove("sidebar-minimized");
        }
      }
      return newState;
    });
  };

  const mobileNav = useMobileBottomNav({
    navigate,
    location,
    toggleSidebar,
    isSidebarMinimized
  });

  const pageContainerStyle = { backgroundColor: "var(--app-bg)" };
  const bannerStyle = {
    position: "fixed",
    zIndex: 106,
    height: BANNER_HEIGHT,
    background: bannerBg,
    color: "#ffffff",
    padding: "8px 12px",
    fontWeight: 600,
    fontSize: "14px",
    borderBottom: "1px solid var(--app-border-soft)",
  };
  const bannerButtonStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "#ffffff",
    color: "#314F71",
    fontWeight: 700,
    fontSize: 13,
    padding: "4px 14px",
    borderRadius: 20,
    textDecoration: "none",
    whiteSpace: "nowrap",
    boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
    transition: "background 0.15s, color 0.15s",
    flexShrink: 0,
  };
  const expiredOverlayStyle = {
    position: "fixed",
    inset: 0,
    zIndex: 9998,
    backgroundColor: themeMode === "dark" ? "rgba(11,11,18,0.84)" : "rgba(255,255,255,0.82)",
    backdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  const expiredCardStyle = {
    textAlign: "center",
    padding: "48px 40px",
    borderRadius: 16,
    background: "var(--app-surface)",
    boxShadow: "0 8px 40px rgba(0,0,0,0.14)",
    maxWidth: 460,
    width: "90%",
  };

  return (
      <div
          className="app-page flex-column flex-column-fluid"
          id="kt_app_page"
          style={pageContainerStyle}
      >
        <MobileBottomNavStyles />

        {bannerMsg && (
          <div
            role="status"
            aria-live="polite"
            className="subscription-banner d-flex align-items-center justify-content-center gap-3"
            style={bannerStyle}
          >
            {bannerMsg}
            {!hasSubscription && (
              <a
                href="/settings/billing"
                style={bannerButtonStyle}
                onMouseEnter={e => { e.currentTarget.style.background = "#eef1f5"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#ffffff"; }}
              >
                Choose a Plan →
              </a>
            )}
          </div>
        )}

        <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">

          <Sidebar
              id="kt_app_sidebar"
              user={user}
              setUser={setData}
              setData={setData}
              type={resolveLoginType(user)}
              collapsed={isSidebarMinimized}
              location={location}
              handleLogout={handleLogout}
              toggleSidebar={toggleSidebar}
              bannerHeight={bannerMsg ? BANNER_HEIGHT : 0}
          />

          <div
            className="app-main flex-column flex-row-fluid app-main--mobile-nav"
            id="kt_app_main"
          >
            <div className="d-flex flex-column flex-column-fluid">
              <div id="kt_app_content">
                <div id="kt_app_content_container" className="app-container container-fluid">
                  {reloadPage ? (
                      <SubUserAccessProvider user={user}>
                        <Wrapper>
                          <Outlet />
                        </Wrapper>
                      </SubUserAccessProvider>
                  ) : (
                      <>
                        <div
                            className="card mb-3 mt-4 px-3"
                            style={{ border: BorderFilter }}
                        >
                          <div className="card-header d-flex justify-content-between align-items-center">
                            <div className="card-title d-flex align-items-center">
                              <Skeleton.Input active />
                            </div>
                            <div className="card-toolbar">
                              <Skeleton.Button active />
                            </div>
                          </div>
                        </div>

                        <div className="card">
                          <div style={{ padding: "0px 15px", marginTop: "10px" }}>
                            <TableLoading
                                id="test-table"
                                columns={[1, 2, 3, 4, 5]}
                                checkBoxVal={true}
                                actions={[1, 2]}
                            />
                          </div>
                        </div>
                      </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <MobileBottomNav
          activeId={mobileNav.activeId}
          onMenu={mobileNav.onMenu}
          onDashboard={mobileNav.onDashboard}
          onProducts={mobileNav.onProducts}
          onCopilot={mobileNav.onCopilot}
        />

        {/* ─── Trial-expired full-page lock ───────────────────────────── */}
        {isTrialExpired && !hasSubscription && location.pathname !== '/settings/billing' && (
          <div
            className="sv-safe-overlay"
            style={expiredOverlayStyle}>
            <div style={expiredCardStyle}>
              <i className="ki-outline ki-lock-2" style={{ fontSize: 52, color: "var(--app-text-muted)", display: "block", marginBottom: 16 }} />
              <h3 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, color: "var(--app-text)" }}>
                Your Trial Has Expired
              </h3>
              <p style={{ color: "var(--app-text-muted)", fontSize: 14, marginBottom: 28, lineHeight: 1.6 }}>
                Your free trial has ended. Upgrade to a paid plan to continue using SellerView and access all features.
              </p>
              <button
                className="btn btn-danger"
                onClick={() => navigate('/settings/billing')}
                style={{ borderRadius: 8, fontWeight: 600, padding: '10px 32px', fontSize: 15 }}
              >
                Upgrade Now
              </button>
            </div>
          </div>
        )}

      </div>
  );
};

export default Page;